import React, { useLayoutEffect, useRef, useCallback } from 'react';
import { SubtitleResizeHandle } from './SubtitleResizeHandle';
import { formatSubtitlePostTextForListDisplay } from '../../../utils/subtitlePostText';
import { useSubtitleResize } from './useSubtitleResize';

type TimelineEditorLike = {
    positionSegments: () => void;
    calculateAndUpdateSpeed: (segment: HTMLElement, subtitleId: number, start: number, end: number) => Promise<void>;
    selectSegment: (segment: HTMLElement, options?: { notifySelection?: boolean }) => void;
    setActiveTrack: (trackType: string) => void;
};

export type TranslatedSegmentModel = {
    id: number;
    subtitle: Task.SubtitlesLegacy;
    startTime: number;
    endTime: number;
};

type Props = {
    segments: TranslatedSegmentModel[];
    /** Which track is “active” in the left rail (translated vs original). */
    timelineActiveTrack: 'translated' | 'original';
    selectedSubtitleId: number | null;
    timelineEditorRef: React.MutableRefObject<TimelineEditorLike | null>;
    getTotalTimeSec: () => number;
};

const TranslatedSegmentRow = React.memo(function TranslatedSegmentRowInner(props: {
    model: TranslatedSegmentModel;
    timelineActiveTrack: 'translated' | 'original';
    isSelected: boolean;
    timelineEditorRef: React.MutableRefObject<TimelineEditorLike | null>;
    getTotalTimeSec: () => number;
}) {
    const { model, timelineActiveTrack, isSelected, timelineEditorRef, getTotalTimeSec } = props;
    const { subtitle, id } = model;
    const rootRef = useRef<HTMLDivElement | null>(null);
    const resizeBaselineRef = useRef<{ start: number; end: number } | null>(null);

    const getSiblingRoots = useCallback(
        () => Array.from(document.querySelectorAll('#translatedTrack .track-content .segment')) as HTMLElement[],
        []
    );

    const onPreviewLayout = useCallback(() => {
        timelineEditorRef.current?.positionSegments();
    }, [timelineEditorRef]);

    const onResizeComplete = useCallback(
        async ({ subtitleId, startSec, endSec }: { subtitleId: number; startSec: number; endSec: number }) => {
            const el = rootRef.current;
            const te = timelineEditorRef.current;
            if (el && te) {
                await te.calculateAndUpdateSpeed(el, subtitleId, startSec, endSec);
            }
        },
        [timelineEditorRef]
    );

    const { onResizePointerDown, isResizing } = useSubtitleResize(rootRef, {
        subtitleId: id,
        getTotalTime: getTotalTimeSec,
        getSiblingSegmentRoots: getSiblingRoots,
        onPreviewLayout,
        onResizeComplete,
    });

    useLayoutEffect(() => {
        const el = rootRef.current;
        if (!el || isResizing) return;

        // 与 props 对齐：上次成功保存后的时间范围作为下一次拖边的基准
        resizeBaselineRef.current = { start: model.startTime, end: model.endTime };

        el.dataset.start = model.startTime.toFixed(3);
        el.dataset.end = model.endTime.toFixed(3);
        el.dataset.subtitleId = String(id);
        el.dataset.originalStart = model.startTime.toFixed(3);
        el.dataset.originalEnd = model.endTime.toFixed(3);
        el.dataset.originalSpeed = (subtitle.audio_speed ?? 1).toString();

        timelineEditorRef.current?.positionSegments();
    }, [id, model.startTime, model.endTime, subtitle.audio_speed, timelineEditorRef, isResizing]);

    const inactive = timelineActiveTrack === 'original';
    const classNames = ['segment', inactive ? 'inactive' : '', isSelected ? 'active' : ''].filter(Boolean).join(' ');

    return (
        <div
            ref={rootRef}
            className={classNames}
            data-subtitle-id={id}
            data-react-translated-segment="1"
            onClick={e => {
                const el = rootRef.current;
                const te = timelineEditorRef.current;
                if (!el || !te) return;
                if ((e.target as HTMLElement).closest('.subtitle-resize-handle')) {
                    return;
                }
                te.setActiveTrack('translated');
                te.selectSegment(el);
            }}
        >
            <SubtitleResizeHandle side="left" onPointerDown={e => onResizePointerDown(e, 'left')} />
            <span>{formatSubtitlePostTextForListDisplay(subtitle.post_text || '')}</span>
            <SubtitleResizeHandle side="right" onPointerDown={e => onResizePointerDown(e, 'right')} />
        </div>
    );
});

/**
 * React-driven “翻译后”轨道片段：声明式渲染 + 无头 resize hook；业务提交仍由 TimelineEditor.calculateAndUpdateSpeed 处理。
 */
export const TranslatedTimelineSegments: React.FC<Props> = ({
    segments,
    timelineActiveTrack,
    selectedSubtitleId,
    timelineEditorRef,
    getTotalTimeSec,
}) => {
    return (
        <>
            {segments.map(model => (
                <TranslatedSegmentRow
                    key={model.id}
                    model={model}
                    timelineActiveTrack={timelineActiveTrack}
                    isSelected={selectedSubtitleId === model.id}
                    timelineEditorRef={timelineEditorRef}
                    getTotalTimeSec={getTotalTimeSec}
                />
            ))}
        </>
    );
};
